import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';

import { DataGrid } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';

import Title from './Title';
import { getColumns } from './utils';
import { PERMISSIONS, REMOVED } from '../../constants';
import {
  searchAnyFieldFilter,
  showErrorNotification,
  stringToBoolean,
} from 'utils/common';
import ConfirmationDialog from 'components/common/ConfirmationDialog';

import { ToolbarContainer } from 'components/common/Containers';
import { ActionButton } from 'components/common/Buttons';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { useSelectionModel } from 'hooks/utils/useSelectionModel';
import {
  DataGridContainer,
  DataGridFilterField,
} from 'components/common/DataTables';
import {
  useGetWLIDListQuery,
  useUpdateWLIDBiddingStatusMutation,
  useForceSyncWLIDMutation,
  useSignInAsMutation,
} from '../../store/api/wl.api';

const ACTION_OPTS = ['us', 'eu']
  .map((center) =>
    [false, true].map((action) => ({
      label: `${action ? 'Disable' : 'Enable'} ${center.toUpperCase()}`,
      'data-data-center': center,
      'data-is-stop-bidding': action.toString(),
    })),
  )
  .flat();

function QuickSearchToolbar(props) {
  return (
    <ToolbarContainer>
      <DataGridFilterField
        variant='standard'
        value={props.value}
        onChange={props.onChange}
        placeholder='Search…'
        InputProps={{
          startAdornment: <SearchIcon fontSize='small' />,
          endAdornment: (
            <IconButton
              title='Clear'
              aria-label='Clear'
              size='small'
              style={{ visibility: props.value ? 'visible' : 'hidden' }}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />

      <Box display='flex' flexDirection='row' flexGrow={1} gap='2rem' px='1rem'>
        {ACTION_OPTS.map(({ label, ...btnProps }, idx) => (
          <ActionButton
            disabled={props.disabled}
            key={idx}
            onClick={props.onOpenBiddingStatusDialog}
            variant='text'
            {...btnProps}
          >
            {label}
          </ActionButton>
        ))}
        <ActionButton
          disabled={props.forceDisabled || props.forceInProgress}
          onClick={props.handleForceSync}
          variant='text'
        >
          {props.forceInProgress ? (
            <CircularProgress size='1rem' color='inherit' />
          ) : null}
          Force Sync
        </ActionButton>
      </Box>

      <Button
        variant='contained'
        color='primary'
        component={Link}
        sx={{ whiteSpace: 'nowrap', m: '5px 5px 0 0' }}
        to='/create'
        disabled={props.disabled}
      >
        Add New
      </Button>
    </ToolbarContainer>
  );
}

const removedFilterFn = ({ status }) => status !== REMOVED;

export default function DataTable() {
  const canEditClients = useHasPermissions(PERMISSIONS.CLIENTS_MANAGEMENT);
  const [searchText, setSearchText] = useState('');
  const { data = [] } = useGetWLIDListQuery();
  const [mutateAsync] = useUpdateWLIDBiddingStatusMutation();
  const [signInAs] = useSignInAsMutation();
  const rows = useMemo(() => {
    const filterFn = searchText
      ? searchAnyFieldFilter(searchText)
      : removedFilterFn;
    return data.filter(filterFn);
  }, [data, searchText]);

  const { enqueueSnackbar } = useSnackbar();

  const { selectionModel, setSelectionModel, clearSelection, getSelectedIds } =
    useSelectionModel(data);
  const [isStopBidding, setStopBidding] = useState(null);
  const [dataCenter, setDataCenter] = useState('');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [forceSync, { isLoading: isSyncing }] = useForceSyncWLIDMutation();

  const handleSignIn = useCallback(
    async (wlid, domain) => {
      if (!domain) return;
      try {
        const data = await signInAs({
          w: wlid,
          d: domain,
        }).unwrap();

        window.open(data.url, '_blank', 'noopener');
      } catch (err) {
        const errMsg =
          err?.response?.data?.error ||
          `Something wrong. Can't sign in to wl-${wlid}`;
        showErrorNotification(errMsg);
      }
    },
    [signInAs],
  );

  const onCloseCompleteDialog = (event) => {
    const { isStopBidding, dataCenter } = event.currentTarget.dataset;
    const isStopBiddingToBool = stringToBoolean(isStopBidding);

    if (!getSelectedIds().length) {
      const biddingStatus = isStopBidding ? 'DISABLE' : 'ENABLE';

      enqueueSnackbar(`Select clients to ${biddingStatus} bidding `, {
        variant: 'warning',
      });
      return;
    }

    setShowCompleteDialog(!showCompleteDialog);
    setStopBidding(isStopBiddingToBool);
    setDataCenter(dataCenter);
  };

  const onHandleChangeBiddingStatus = async () => {
    setShowCompleteDialog(false);
    const clientIds = getSelectedIds();
    if (!clientIds.length) {
      return;
    }
    try {
      await mutateAsync({ clientIds, isStopBidding, dataCenter }).unwrap();
      clearSelection();
      enqueueSnackbar(`Status updated successfully`, { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(`Failed to update bidding status`, { variant: 'error' });
    }
  };

  const handleForceSync = async () => {
    const clientIds = getSelectedIds();
    if (!clientIds.length) {
      return;
    }
    try {
      const { messages, errors } = await forceSync(clientIds).unwrap();
      messages.forEach((message) =>
        enqueueSnackbar(message, { variant: 'success' }),
      );
      errors.forEach((message) =>
        enqueueSnackbar(message, { variant: 'error' }),
      );
    } catch (err) {
      console.error(`Forced client sync failed`, err);
      enqueueSnackbar({ message: 'Clients sync failed', variant: 'error' });
    }
  };

  return (
    <React.Fragment>
      <Box>
        <Title>Clients</Title>
      </Box>

      <ConfirmationDialog
        title='Clients bidding'
        contentText='By clicking "Approve", you confirm changing bidding status'
        isOpen={showCompleteDialog}
        handleConfirm={onHandleChangeBiddingStatus}
        handleClose={onCloseCompleteDialog}
      />

      <DataGridContainer>
        <DataGrid
          rows={rows || []}
          hasFocus={false}
          columns={getColumns({ handleSignIn })}
          checkboxSelection={canEditClients}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterPagination
          showToolbar
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          getRowClassName={(params) =>
            params.row.status === 'removed' ? 'disabled' : ''
          }
          sortModel={[{ field: 'wlid', sort: 'desc' }]}
          slots={{ toolbar: QuickSearchToolbar }}
          slotProps={{
            toolbar: {
              value: searchText,
              onChange: (event) => setSearchText(event.target.value),
              clearSearch: () => setSearchText(''),
              onOpenBiddingStatusDialog: onCloseCompleteDialog,
              disabled: !canEditClients,
              forceDisabled: !(canEditClients && getSelectedIds().length > 0),
              forceInProgress: isSyncing,
              handleForceSync: handleForceSync,
            },
          }}
        />
      </DataGridContainer>
    </React.Fragment>
  );
}
